import { useLocation } from "react-router-dom";
import AppBar from "../../common/AppBar.js";

import { formatName } from "../../util/AppUtils.jsx";
import "./SubassemblyDetails.css";
import ZoomControls from "../../common/ZoomControls.js";
import { useState } from "react";

const VersionHistoryRevisionsTable = () => {
  const location = useLocation();

  const {
    type,
    partNumber,
    revision,
    revisionData,
    description,
    status,
    revisionId,
    savedDetailsForReviewing,
  } = location.state;

  const [zoomLevel, setZoomLevel] = useState(1);

  const handleZoom = (factor) => {
    const rawZoom = zoomLevel * factor;
    const clampedZoom = Math.max(0.1, Math.min(rawZoom, 5)); // Clamp between 0.1 and 5
    const roundedZoom = Math.round(clampedZoom * 10) / 10; // Round to nearest 0.1 (10%)
    setZoomLevel(roundedZoom);
  };

  const handleManualZoom = (e) => {
    const input = parseFloat(e.target.value);
    if (!isNaN(input)) {
      const clampedInput = Math.max(10, Math.min(input, 500)); // Clamp 10% to 500%
      const roundedInput = Math.round(clampedInput / 10) * 10; // Nearest multiple of 10
      setZoomLevel(roundedInput / 100); // Store as decimal
    }
  };

  return (
    <div>
      <div
        className="version-history-appbar"
        style={{ position: "relative", width: "100%" }}
      >
        <AppBar
          subTitleAboveTitle={
            <>
              {localStorage.getItem("user_name")} is Reviewing{" "}
              <span style={{ color: "#0D6EFD" }}>
                {savedDetailsForReviewing}
              </span>
            </>
          }
          title={`${type} ${partNumber}, Rev ${revision}`}
          showBlackTitle={true}
          showTablesDropdown={false}
          showProfileButton={false}
          buttons={[{ label: "Preview", path: "/preview" }]}
        />
        <style>
          {`
            /* Override AppBar styles for VersionHistoryRevisionsTable only */
            .version-history-appbar .appbar h2 {
              position: static !important;
              left: auto !important;
              transform: none !important;
              margin: 0 !important;
            }
            
            .version-history-appbar .appbar > div {
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              flex-direction: column !important;
              flex: 1 !important;
              text-align: center !important;
              gap: 8px !important; /* Add spacing between subtitle and title */
            }
            
            .version-history-appbar .appbar > div p {
              margin: 0 !important;
              font-size: 14px !important;
              color: #666 !important;
              order: 1 !important; /* Ensure subtitle appears first */
            }
            
            .version-history-appbar .appbar > div h2 {
              font-size: 25px !important;
              margin: 0 !important;
              order: 2 !important; /* Ensure title appears second */
            }

            .version-history-appbar .appbar {
              position: relative !important;
              padding-right: 0 !important; /* Remove right padding */
            }

            .version-history-appbar .appbar-buttons {
              position: fixed !important;
              right: 32px !important; /* or 16px for even closer */
              top: 18px; /* adjust as needed to vertically center */
              margin-left: 0 !important;
              z-index: 10;
              background: white; /* to avoid overlap artifacts */
            }
          `}
        </style>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 0",
          flexWrap: "wrap", // for responsiveness
        }}
      >
        {/* Checkbox + Title + Description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              margin: 0,
            }}
          >
            <span style={{ fontSize: "16px", fontWeight: 600 }}>
              {type} {partNumber}
            </span>
          </label>

          {/* Description */}
          <span style={{ marginLeft: "2px", marginTop: "5px", color: "#666" }}>
            Description: {description === "-" ? "" : description}
          </span>
        </div>

        {/* Right-aligned section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginLeft: "auto",
          }}
        >
          <div>
            <strong>Status: </strong>
            <span style={{ color: "#a6a6a6" }}>{location.state?.status}</span>
          </div>

          <div>
            <strong>Revision: </strong>
            <span>{revision}</span>
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h3 style={{ marginBottom: "-7px" }}>Revisions Table</h3>
        <div className="button-container" style={{ margin: 0 }}></div>
      </div>
      <table className="table" style={{ marginTop: "-10px" }}>
        <thead>
          <tr>
            <th>Rev</th>
            <th>Date Released</th>
            <th>Revision Details</th>
            <th>Submitter / Requestor</th>
            <th>Approver</th>
            <th>References</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {[...revisionData]
            .sort((a, b) => {
              const parseRevision = (rev) => {
                const match = rev.match(/^([A-Z])(?:-(\d*)|(\d+)([a-z]?))?$/);
                if (!match) return null;
                const [, letter, dashMinor, num, amendChar] = match;
                return {
                  base: letter,
                  minorNum: dashMinor !== undefined ? 0 : parseInt(num || "0"),
                  amendChar: amendChar || "",
                };
              };

              const revA = parseRevision(a.rev);
              const revB = parseRevision(b.rev);
              if (!revA || !revB) return 0;

              if (revA.base !== revB.base)
                return revA.base < revB.base ? -1 : 1;
              if (revA.minorNum !== revB.minorNum)
                return revA.minorNum - revB.minorNum;
              return revA.amendChar.localeCompare(revB.amendChar);
            })
            .map((rev, index) => {
              // Flatten all required_by entries from status_changes
              const requiredBy = "required_by" in rev ? rev.required_by : [];

              // Find users by purpose
              const submitter = requiredBy.find(
                (rb) => rb.purpose?.toLowerCase() === "submit"
              )?.user;

              const approvers = requiredBy
                .filter((rb) => rb.purpose?.toLowerCase() === "approve")
                .map((rb) => rb.user)
                .filter(Boolean); // filter out nulls

              const isCurrentOpnRev = rev.procedure_revision_id === revisionId;
              const showNavLink =
                !rev.is_maintained_outside_system && !isCurrentOpnRev
                  ? true
                  : false;

              return (
                <tr
                  key={index}
                  className={`${isCurrentOpnRev && "hilight-current-revision"}`}
                >
                  <td>{rev.rev || "-"}</td>
                  <td>
                    {rev.date_released
                      ? new Date(rev.date_released).toLocaleDateString(
                          "ja-JP",
                          {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                          }
                        )
                      : "-"}
                  </td>
                  <td>{rev.revision_details || "-"}</td>
                  <td>
                    {requiredBy
                      .filter((rb) => rb.purpose?.toLowerCase() === "submit")
                      .map((rb, i) => {
                        const name = formatName(rev, rb.user);
                        return (
                          <div key={i}>
                            {!rb.is_open
                              ? `Submitted by ${name}`
                              : `Pending Submission from ${name}`}
                          </div>
                        );
                      })}
                  </td>

                  <td>
                    {requiredBy
                      .filter((rb) => rb.purpose?.toLowerCase() === "approve")
                      .map((rb, i) => {
                        const name = formatName(rev, rb.user);
                        return (
                          <div key={i}>
                            {!rb.is_open
                              ? `Approved by ${name}`
                              : `Pending Approval from ${name}`}
                          </div>
                        );
                      })}
                  </td>
                  <td>{rev.reference || "-"}</td>
                  <td>
                    {rev.status_changes?.find((sc) => sc.is_latest)
                      ?.status_changed_to || "-"}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>

      <ZoomControls
        zoomLevel={zoomLevel}
        onZoom={handleZoom}
        onManualZoom={handleManualZoom}
      />
    </div>
  );
};
export default VersionHistoryRevisionsTable;
